import React, { useId, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Paywall from "../../../common/Paywall";
import { InsightBox } from "../../../ForBrands/ui/BrandUI";
import { MobileTabBar } from "../../../ForBrands/ui/mobile/MobileShell";
import { TeamChips, ValueContextCard, recruitingLinksOf } from "../profileSections";
import PerformanceAttribution from "../../../common/PerformanceAttribution";
import AthletePerformance from "../performance/AthletePerformance";
import useAthletePerformance from "../performance/useAthletePerformance";
import { hasPerformanceV2, performanceAttributed } from "../performance/athletePerformanceModel";
import {
  BioCard,
  BoxScoreCard,
  BrandAlignmentCard,
  CircleCard,
  ContributionsCard,
  DetailsCard,
  EngagementCard,
  MobileRangeCard,
  RadarCard,
  RateCard,
  RecruitingLinksCard,
  ScoreCard,
  SeasonCard,
  ShootingCard,
  StaticChip,
  ValueTiles,
  VisibilityCard,
} from "./mobileProfileSections";

// My Profile on phones (Figma 2000:373 Overview, 2000:467 Performance,
// 2000:558 Visibility, 2000:649 Value, 2000:740 About). ProfileBody renders
// this instead of the one-page layout inside the phone theme: the athlete's
// own page in Athlete Mode and the athlete profile in Coach Mode. It takes
// ProfileBody's props plus:
//
// narratives      profileNarratives() for this model and reader
// overviewHeader  the page's identity row and actions, top of Overview
//
// The open tab lives in ?tab= (replacing the history entry), so Back and a
// refresh keep it and a link can open a tab; overview is the default and
// leaves the URL clean. The tab bar sticks under the 55px top bar and
// changing tab scrolls the page back to the top.

export const PROFILE_TABS = [
  { value: "overview", label: "Overview" },
  { value: "performance", label: "Performance" },
  { value: "visibility", label: "Visibility" },
  { value: "value", label: "Value" },
  { value: "about", label: "About" },
];

export const DEFAULT_PROFILE_TAB = "overview";

// The tab a ?tab= value opens; anything unknown opens Overview.
export const profileTabFor = (value) => (PROFILE_TABS.some((t) => t.value === value) ? value : DEFAULT_PROFILE_TAB);

const OverviewTab = ({ model, narratives, premium, header }) => (
  <>
    {header}
    <ScoreCard model={model} />
    <ContributionsCard contributions={model.contributions} />
    <ValueTiles model={model} tierLabel={narratives.tierLabel} premium={premium} />
  </>
);

// The sport, the box score and its shooting, the player radar (League /
// College / Pro) and summary, then the current season and its outlook:
// the desktop sections and conditions, in the phone cards. No level draws
// the shot zone chart (generated dots, not real shots). Not paywalled, as
// on desktop. With two or more play groups the box score's School |
// Grassroots switch picks the line its cells, the shooting card and the
// summary read (D7); the radar keeps its own group, which it names.
const ProfilePerformance = ({ model, narratives }) => {
  const [picked, setPicked] = useState(null);
  const { self, cs, showStats, seasonAi } = narratives;
  const line = narratives.boxLine ? narratives.boxLine(picked) : model;
  const subtitle = narratives.boxScoreSubtitleFor ? narratives.boxScoreSubtitleFor(line) : narratives.boxScoreSubtitle;
  const performanceSummary = narratives.performanceSummaryFor ? narratives.performanceSummaryFor(line) : narratives.performanceSummary;
  return (
    <>
      {model.sport && showStats && <StaticChip>{model.sport}</StaticChip>}
      <BoxScoreCard model={model} line={line} subtitle={subtitle} self={self} onGroupChange={setPicked} />
      {line.hasBoxScore && (
        <>
          <ShootingCard items={line.shooting} hasPercentiles={line.hasPercentiles} />
          <RadarCard groups={model.radarGroups} />
          {performanceSummary && (
            <InsightBox compact title={self ? "Your season summary" : "Season summary"}>
              {performanceSummary}
            </InsightBox>
          )}
        </>
      )}
      {cs && (
        <>
          <SeasonCard model={model} narratives={narratives} />
          {seasonAi && (
            <InsightBox compact title={self ? "Keep improving" : "Season outlook"}>
              {seasonAi}
            </InsightBox>
          )}
        </>
      )}
    </>
  );
};

// Jayson's performance cards when the profile has them (performanceV2; spec
// B2), in their phone looks, else the profile's own cards above, which also
// stand in when the section turns out to be unavailable; then, once and
// last, the data attribution line over the league feed's data
// (common/PerformanceAttribution, Kobi 10/21). `perf` is the tabs'
// useAthletePerformance(), so a tab switch never asks again.
const PerformanceTab = ({ model, narratives, perf }) => {
  const own = <ProfilePerformance model={model} narratives={narratives} />;
  return (
    <>
      {hasPerformanceV2(model) ? <AthletePerformance perf={perf} model={model} narratives={narratives} fallback={own} /> : own}
      {performanceAttributed({ model, perf }) && <PerformanceAttribution />}
    </>
  );
};

// The valuation cards behind the same three Paywalls as desktop (the one
// value card, D3: CacheValue, or PerformanceValue in its place for an
// athlete who has not onboarded, with CacheNILx; the rate card; CacheValue
// in context, which a PerformanceValue profile does not have), then the
// brand fit and the brand alignment read, which desktop does not paywall
// either. The context card is inline, so there is no (i) dot to open it.
const ValueTab = ({ model, narratives, premium, matches }) => {
  const { self, tierLabel, hasMatches, matching, brandNarrative } = narratives;
  const card = model.valueCard;
  return (
    <>
      <Paywall
        requiredEntitlements={premium}
        title={card.paywallTitle}
        description="Upgrade to view market valuation and social influence value."
        showPreview
        showLockIcon={false}
      >
        <div className="flex flex-col gap-[18px]">
          {/* The rank in the head, the group it ranks against under it when the badge does not say "nationally"; PerformanceValue has no badge. */}
          <MobileRangeCard
            title={card.title}
            sub={card.subtitle}
            badge={card.badgeRank ?? card.badge}
            badgeNote={card.badgeNote}
            range={card.range}
          />
          <MobileRangeCard title="CacheNILx™ Value" sub="Social influence value" badge={tierLabel} range={model.cacheNilx} />
        </div>
      </Paywall>
      {model.nilRates.length > 0 && (
        <Paywall
          requiredEntitlements={premium}
          title="Unlock the NILx™ Rate Card"
          description="Upgrade to view the full NIL rate card."
          showPreview
          showLockIcon={false}
        >
          <RateCard rates={model.nilRates} />
        </Paywall>
      )}
      {model.context && (
        <Paywall
          requiredEntitlements={premium}
          title={self ? "Unlock your CacheValue™ in context" : "Unlock CacheValue™ in context"}
          description={self ? "Upgrade to see where your valuation sits nationally." : "Upgrade to see where this valuation sits nationally."}
          showPreview
          showLockIcon={false}
        >
          <ValueContextCard model={model} framed compact selfView={self} />
        </Paywall>
      )}
      {(model.brand || matching || hasMatches) && (
        <>
          <BrandAlignmentCard model={model} narratives={narratives} matches={matches} />
          <InsightBox compact title="Brand alignment" subtitle="From interests, NIL deals and work experience">
            {brandNarrative}
          </InsightBox>
        </>
      )}
    </>
  );
};

const VisibilityTab = ({ model, narratives, onEdit }) => (
  <>
    <VisibilityCard platforms={model.platforms} onAdd={onEdit ? () => onEdit("socials") : undefined} />
    <EngagementCard engagement={narratives.engagement} />
    <InsightBox compact title={narratives.visibilityTitle}>
      {narratives.visibilityNarrative}
    </InsightBox>
  </>
);

// TeamChips sit under the details card (they have no place of their own in
// the frames), then the recruiting links (247Sports, On3) when there are any.
const AboutTab = ({ model, athlete, selfView, onEdit }) => (
  <>
    <BioCard bio={model.bio} onEdit={onEdit} />
    <DetailsCard fields={model.profileFields} />
    <TeamChips teams={athlete.allTeams} />
    <RecruitingLinksCard links={recruitingLinksOf(model.socialMedia)} />
    <CircleCard circle={model.circle} selfView={selfView} />
  </>
);

const ProfileTabsMobile = ({
  model,
  athlete = {},
  narratives,
  premium,
  selfView = false,
  matches,
  onEdit,
  overviewHeader,
}) => {
  const [params, setParams] = useSearchParams();
  const tab = profileTabFor(params.get("tab"));
  // Jayson's cards load once the Performance tab opens (not with Overview).
  const perf = useAthletePerformance({
    athleteId: model.athleteId,
    email: athlete?.email,
    enabled: hasPerformanceV2(model) && tab === "performance",
  });
  const uid = useId();
  const tabId = (value) => `${uid}-tab-${value}`;
  const panelId = `${uid}-panel`;

  const select = (value) => {
    if (value === tab) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === DEFAULT_PROFILE_TAB) next.delete("tab");
        else next.set("tab", value);
        return next;
      },
      { replace: true },
    );
    if (typeof window !== "undefined" && typeof window.scrollTo === "function") window.scrollTo(0, 0);
  };

  return (
    <div className="flex flex-col">
      <MobileTabBar
        tabs={PROFILE_TABS.map((t) => ({ ...t, id: tabId(t.value), controls: t.value === tab ? panelId : undefined }))}
        value={tab}
        onChange={select}
        ariaLabel="Profile sections"
        sticky
        className="-mx-6 -mt-5"
      />
      {/* Overview carries the visible name; the other tabs keep a page heading for screen readers. */}
      {tab !== "overview" && <h1 className="sr-only">{model.fullName}</h1>}
      <div role="tabpanel" id={panelId} aria-labelledby={tabId(tab)} className="flex flex-col gap-[18px] pt-5">
        {tab === "overview" && <OverviewTab model={model} narratives={narratives} premium={premium} header={overviewHeader} />}
        {tab === "performance" && <PerformanceTab model={model} narratives={narratives} perf={perf} />}
        {tab === "visibility" && <VisibilityTab model={model} narratives={narratives} onEdit={onEdit} />}
        {tab === "value" && <ValueTab model={model} narratives={narratives} premium={premium} matches={matches} />}
        {tab === "about" && <AboutTab model={model} athlete={athlete} selfView={selfView} onEdit={onEdit} />}
      </div>
    </div>
  );
};

export default ProfileTabsMobile;
